import { useCallback, useEffect, useRef, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { STRINGS } from '@/strings/strings';
import { ApiError } from '@/lib/api-client';
import type { PreviewEnvelope } from '../../types';
import { releaseSummaryReview, retrySummaryReview, selfWriteSummaryReview } from '../../api';
import {
  SUMMARY_CUSTOMER_CHANGE_MAX_LENGTH,
  TITELWUNSCH_MAX_LENGTH,
} from '../../state/book-configurator-store';
import { SUMMARY_TEXT_MAX_LENGTH } from '../../constants/text-limits';
import { CharacterCounter } from '../geschichte/CharacterCounter';
import { Eyebrow } from '../Eyebrow';
import { VerificationPendingBlock } from './VerificationPendingBlock';

/**
 * Position-5 Commit 6b.3 (Sebastian ruling 2026-09-02) — summary review
 * screen for status = summary_review.
 *
 * Post-queue: retry and self-write are fire-and-forget. The endpoints
 * enqueue and return 202. This screen reads `envelope.summaryReview.
 * retryInFlight` / `selfWriteInFlight` on every polling tick and shows
 * `retryLoading` / `selfWriteLoading` respectively for as long as the
 * flag stays true. Two-flag semantics at the wire come from a single
 * enum column on BookOrder (see backend SummaryReviewProjection JSDoc
 * for why); the two flags are mutually exclusive by construction.
 *
 * Branch priority (unchanged from 6b): a third-retry failure sets BOTH
 * capReached and generationFailed — self-write wins so the customer
 * sees the only working way forward instead of a dead retry button.
 *
 *   self-write        capReached === true
 *                     (generationFailed banner appears inline above
 *                      the textarea if the third retry failed)
 *   generation-failed generationFailed === true AND
 *                     summaryText === '' (retry-only surface)
 *   generating        summaryText === '' AND generationFailed === false
 *   ready-to-review   summaryText non-empty AND capReached === false
 *
 * Loading state combines TWO sources:
 *   - Local pending: covers the enqueue-click round-trip window
 *     (~milliseconds). Set true on click, cleared on 202 or on error.
 *   - Projection in-flight: covers the queued-run window that starts
 *     after the 202 lands and continues until the worker's atomic
 *     write clears the flag. Polled envelope is the source of truth.
 *   The button is disabled while EITHER source is true. Without the
 *   projection source the screen would fall back to ready-to-review
 *   immediately after the 202 and invite a double submit.
 *
 * The attempt indicator reads envelope.summaryReview.runCount only —
 * the local `retryRunCount` optimistic-update from 6b is retired
 * because the 202 response no longer carries a count; polling
 * refreshes the number when the worker writes.
 */

export interface SummaryReviewScreenProps {
  readonly envelope: PreviewEnvelope;
  readonly onRefetch: () => void;
}

type LocalActionState =
  | { readonly kind: 'idle' }
  | { readonly kind: 'pending' }
  | { readonly kind: 'error'; readonly message: string };

const S = STRINGS.book.summaryReview;

// PWA-side mirror of the SUMMARY_RUN_CAP backend constant. Kept local
// so the screen never diverges from what the envelope's `capReached`
// already computes — this value is only used for the attemptIndicator
// interpolation, where the backend cap is a display concern.
const SUMMARY_RUN_CAP = 3;

export function SummaryReviewScreen({ envelope, onRefetch }: SummaryReviewScreenProps) {
  // envelope.summaryReview is populated by the backend at
  // status = summary_review. Falling through to defaults keeps the
  // screen safe if the envelope arrives without it (transitional
  // deploys); the polling tick will replace with real values within 5s.
  const runCount = envelope.summaryReview?.runCount ?? 0;
  const capReached = envelope.summaryReview?.capReached ?? false;
  const generationFailed = envelope.summaryReview?.generationFailed ?? false;
  const retryInFlight = envelope.summaryReview?.retryInFlight ?? false;
  const selfWriteInFlight = envelope.summaryReview?.selfWriteInFlight ?? false;
  // Paket 1 (Sebastian ruling 2026-09-10) — V7 pre-model refusal
  // signal. True when the last pre-model run refused
  // (INHALTSFILTER / VERSTÄNDLICHKEIT). Drives the refused-state
  // branch below: customer sees the placeholder banner + retry
  // surface, no Passt so. Priority is BELOW capReached so the
  // self-write escape hatch still fires on run 3 regardless of
  // whether that run refused (existing three-run limit + self-write
  // fallback stay unchanged per Paket 1 §4).
  const refused = envelope.summaryReview?.refused ?? false;
  // Sebastian ruling 2026-09-21 (follow-up 2, Option A) — once the
  // customer has self-edited the summary, the change-request path
  // disappears from the screen. Only further self-edits within the
  // shared 3-attempt budget and "Passt so" remain. Backend enforces
  // the same rule as a hard-stop on the retry endpoint.
  const selfWriteHasOccurred = envelope.summaryReview?.selfWriteHasOccurred ?? false;
  const summaryText = envelope.zusammenfassung;

  const [customerChange, setCustomerChange] = useState('');
  // Refused-screen title input (Sebastian ruling 2026-09-10 late
  // evening). Seeded once at mount from envelope.title — at
  // summary_review this equals intake.titelwunsch per preview-read
  // .service.ts:1105-1112 (storyText.title is null pre-story-gen).
  // Local state; persists across refused → refused cycles so the
  // customer's typed value survives a failed retry. On successful
  // retry the whole screen unmounts (status transitions away), so
  // no explicit reset is needed.
  const [refusedTitle, setRefusedTitle] = useState(() => envelope.title ?? '');
  // Captured once at mount — the seed value for refusedTitle. Lets the
  // submit button distinguish "customer edited the title" from "customer
  // saw the prefill and left it alone." The refused screen enables
  // submit when EITHER the title or the description was edited from
  // its initial state.
  const initialRefusedTitleRef = useRef<string>(envelope.title ?? '');
  const [retryClickState, setRetryClickState] = useState<LocalActionState>({ kind: 'idle' });
  const [releaseState, setReleaseState] = useState<LocalActionState>({ kind: 'idle' });
  const [selfWriteText, setSelfWriteText] = useState('');
  // Sebastian ruling 2026-09-21 — self-write is available from the
  // first view. Local toggle drives the inline editor; when the
  // customer clicks "Zusammenfassung selbst bearbeiten", the toggle
  // flips true, the textarea is prefilled with the current
  // summaryText, and the submit stays on the summary screen (no
  // transition to anchor on success).
  const [selfWriteOpen, setSelfWriteOpen] = useState(false);
  const [selfWriteClickState, setSelfWriteClickState] = useState<LocalActionState>({
    kind: 'idle',
  });
  const openSelfWrite = useCallback(() => {
    // Prefill with the current summary — Sebastian's ruling: "the
    // editor opens prefilled with the current summary, so the
    // customer edits instead of writing from scratch." Envelope
    // field is `zusammenfassung` (aliased locally to `summaryText`
    // above).
    setSelfWriteText(envelope.zusammenfassung ?? '');
    setSelfWriteClickState({ kind: 'idle' });
    setSelfWriteOpen(true);
  }, [envelope.zusammenfassung]);
  const closeSelfWrite = useCallback(() => {
    setSelfWriteOpen(false);
    setSelfWriteClickState({ kind: 'idle' });
  }, []);
  // Sebastian ruling 2026-09-21 (follow-up) — change-request area
  // sits BELOW the primary edit button as a collapsed alternative.
  // Opens on click of `Oder sag uns, was anders sein soll`.
  const [changeRequestOpen, setChangeRequestOpen] = useState(false);
  const openChangeRequest = useCallback(() => {
    setChangeRequestOpen(true);
  }, []);
  // Sebastian ruling 2026-09-21 — auto-close the editor after the
  // customer's submission completes. `selfWriteInFlight` transitions
  // false→true when the worker picks up the job, and true→false
  // when the worker completes. The true→false transition is the
  // signal that a submit finished — close the editor so the customer
  // sees the summary block reflect the new text (via envelope
  // refetch). The editor stays open across the polling window so
  // the loading state on the submit button is visible throughout.
  const prevInFlightRef = useRef(false);
  useEffect(() => {
    if (prevInFlightRef.current && !selfWriteInFlight) {
      setSelfWriteOpen(false);
      setSelfWriteText('');
    }
    prevInFlightRef.current = selfWriteInFlight;
  }, [selfWriteInFlight]);

  // Combined pending: local click round-trip OR the queue-and-poll
  // window signalled by the envelope. See file header for why both
  // sources are needed.
  const retryPending = retryClickState.kind === 'pending' || retryInFlight;
  const selfWritePending = selfWriteClickState.kind === 'pending' || selfWriteInFlight;

  // Sebastian ruling 2026-09-03 — handler unconditionally POSTs.
  // State 4 (main review) still enforces "type something first" via
  // the button's `disabled` prop below (line ~315). State 2 (first-
  // run generation failed) renders NO textarea and needs the button
  // to POST with an empty `customerChange`; the retry endpoint's
  // DTO (`summary-review/dto/retry.dto.ts`) accepts empty. The
  // previous handler-level `.trim().length === 0` early-return was
  // redundant in State 4 (button already disabled) and destructive
  // in State 2 (button enabled, textarea absent — every click
  // silently no-op'd, leaving orders b3cd3130 / 59285bff stuck).
  // Optional `titelwunschOverride` (Sebastian ruling 2026-09-10 late
  // evening). Only the refused-screen branch passes it — State 2 (first-
  // run generation failed) and State 4 (main review) call handleRetry()
  // without a title override, preserving their existing single-field
  // semantics. The backend treats undefined as "do not touch intake",
  // empty string as "erase intake.titelwunsch", non-empty as
  // "write trimmed value into intake atomically alongside the flag."
  const handleRetry = useCallback(
    async (titelwunschOverride?: string) => {
      setRetryClickState({ kind: 'pending' });
      try {
        await retrySummaryReview(envelope.bookOrderId, customerChange, titelwunschOverride);
        // Enqueue landed — clear the textarea and drop back to idle so
        // the button shows retryLoading via the projection (retryInFlight
        // is now true on the next envelope tick) rather than via local
        // state. onRefetch fires the immediate re-poll so the projection
        // arrives without waiting for the polling cadence.
        setCustomerChange('');
        setRetryClickState({ kind: 'idle' });
        onRefetch();
      } catch (err) {
        const code = err instanceof ApiError ? err.errorCode : null;
        // fix/text-field-limits (Sebastian ruling 2026-09-29 §4) —
        // when the backend returns a length rejection on customerChange
        // (client-side maxLength somehow bypassed), swap the raw
        // class-validator string for the customer-facing fallback.
        // Same detection as handleSelfWrite below.
        if (
          err instanceof ApiError &&
          code === 'validation_error' &&
          typeof err.envelope?.user_message === 'string' &&
          err.envelope.user_message.includes('shorter than or equal')
        ) {
          setRetryClickState({
            kind: 'error',
            message: STRINGS.book.textLimits.backendLengthErrorFallback,
          });
          return;
        }
        // titelwunsch_blocked (Sebastian ruling 2026-09-10 late evening) —
        // the same deterministic blocklist gate that fires at create-order
        // now fires on retry. Surface the verbatim user_message from the
        // envelope so the customer sees the same one-liner as on Personen.
        const message =
          code === 'titelwunsch_blocked' &&
          err instanceof ApiError &&
          typeof err.envelope?.user_message === 'string' &&
          err.envelope.user_message.length > 0
            ? err.envelope.user_message
            : code === 'summary_review_generation_failed'
              ? S.generationFailed
              : code === null
                ? S.retryErrorProviderDown
                : S.retryErrorGeneric;
        setRetryClickState({ kind: 'error', message });
      }
    },
    [customerChange, envelope.bookOrderId, onRefetch],
  );

  const handleRelease = useCallback(async () => {
    setReleaseState({ kind: 'pending' });
    try {
      await releaseSummaryReview(envelope.bookOrderId);
      // Release stays synchronous — do not clear state, refetch flips
      // the status to anchor_pending and BookRoute re-dispatches. The
      // screen unmounts.
      onRefetch();
    } catch (err) {
      const code = err instanceof ApiError ? err.errorCode : null;
      const message =
        code === 'summary_review_generation_failed' ? S.generationFailed : S.retryErrorGeneric;
      setReleaseState({ kind: 'error', message });
    }
  }, [envelope.bookOrderId, onRefetch]);

  const handleSelfWrite = useCallback(async () => {
    const trimmed = selfWriteText.trim();
    if (trimmed.length === 0) return;
    setSelfWriteClickState({ kind: 'pending' });
    try {
      // Sebastian's verbatim rule: the customer's text is submitted
      // as typed, minus only leading/trailing whitespace. No
      // reformatting, no auto-capitalisation.
      await selfWriteSummaryReview(envelope.bookOrderId, trimmed);
      // Enqueue landed — drop the local pending state; the
      // projection's `selfWriteInFlight` (true on next tick) drives
      // the loading label on the still-open editor. The editor
      // auto-closes when in-flight transitions false again (see the
      // effect below). Sebastian ruling 2026-09-21 — the order
      // STAYS at summary_review after self-write.
      setSelfWriteClickState({ kind: 'idle' });
      onRefetch();
    } catch (err) {
      // Jugendschutz self-write blocklist (Sebastian ruling
      // 2026-09-10 evening) — verbatim `user_message` from the
      // backend refusal (`error_code: 'self_write_blocked'`). Rendered
      // in red under the textarea by the existing error slot; the
      // customer's typed text stays intact (local state is not
      // cleared) so the customer can edit + resubmit.
      if (
        err instanceof ApiError &&
        err.errorCode === 'self_write_blocked' &&
        typeof err.envelope?.user_message === 'string' &&
        err.envelope.user_message.length > 0
      ) {
        setSelfWriteClickState({ kind: 'error', message: err.envelope.user_message });
        return;
      }
      const code = err instanceof ApiError ? err.errorCode : null;
      // fix/text-field-limits (Sebastian ruling 2026-09-29 §4) —
      // when the backend still returns a length rejection (client-
      // side maxLength somehow bypassed), swap the raw class-
      // validator string for the customer-facing fallback. Detected
      // via validation_error + the "shorter than or equal" substring
      // in the wire user_message (the class-validator @MaxLength
      // default). Kept as a substring check (not an exact match) so
      // future @MaxLength overrides with an override message still
      // route here as long as they contain the substring.
      if (
        err instanceof ApiError &&
        code === 'validation_error' &&
        typeof err.envelope?.user_message === 'string' &&
        err.envelope.user_message.includes('shorter than or equal')
      ) {
        setSelfWriteClickState({
          kind: 'error',
          message: STRINGS.book.textLimits.backendLengthErrorFallback,
        });
        return;
      }
      const message =
        code === 'summary_review_generation_failed' ? S.generationFailed : S.retryErrorGeneric;
      setSelfWriteClickState({ kind: 'error', message });
    }
  }, [envelope.bookOrderId, onRefetch, selfWriteText]);

  // Sebastian ruling 2026-09-21 — the previous capReached-only branch
  // that hijacked the whole screen to force self-write is retired.
  // Self-write now sits as an inline toggle inside State 4 (below).
  // The remaining terminal-cap surface is the support-line variant
  // for the two hard-stop cases (selfWriteBlockedByTitle +
  // selfWriteRefusalCapReached); those two do end the flow, so the
  // support-line short-circuit stays.
  if (
    envelope.summaryReview?.selfWriteBlockedByTitle === true ||
    envelope.summaryReview?.selfWriteRefusalCapReached === true
  ) {
    return (
      <div className="mx-auto flex max-w-2xl flex-col gap-6 p-4 text-dream-navy">
        <div className="flex flex-col gap-2">
          <h1 className="text-2xl font-semibold">{S.selfWrite.heading}</h1>
        </div>
        <p
          role="status"
          aria-live="polite"
          className="rounded-lg border border-error/40 bg-error/10 p-4 text-base"
        >
          {S.runOutOfAttemptsSupportLine}
        </p>
      </div>
    );
  }

  // ── State 1b: refused (Paket 1, Sebastian ruling 2026-09-10) ─────
  //
  // V7 pre-model refused the customer's brief (INHALTSFILTER
  // inappropriate-content or VERSTÄNDLICHKEIT invalid-input). Sits
  // AFTER capReached so the self-write escape hatch still fires when
  // the third run refused. Customer sees the refusal banner + retry
  // surface; Passt so is not rendered on this screen (the customer's
  // only affordance is edit + retry per Paket 1 §4).
  //
  // Paket 1 amendment (Sebastian ruling 2026-09-10 evening) — screen
  // uses refused-specific STRINGS everywhere the reused State-4
  // wording would have presumed a prior summary to change:
  //   - screenHeading replaces S.heading ("Deine Zusammenfassung" →
  //     "Das lässt sich so nicht erzählen")
  //   - changeFieldLabel replaces S.changeFieldLabel ("Was soll
  //     anders sein?" → "Worum soll es gehen?")
  //   - attemptIndicator replaces S.attemptIndicator ("Änderung n
  //     von 3" → "Versuch n von 3")
  //   - three refused example lines replace the State-4 change-request
  //     examples
  //   - refused changeFieldPlaceholder replaces the State-4 placeholder
  // Book-title element dropped: if the refusal was the Titelwunsch,
  // showing it here would look like approval; if it was the description,
  // it is noise (Sebastian ruling). Eyebrow, examplesLead, cap-warning
  // and retryButton reused as-is.
  if (refused) {
    const isLastRunRefused = runCount === SUMMARY_RUN_CAP - 1;
    return (
      <div className="mx-auto flex max-w-2xl flex-col gap-6 p-4 text-dream-navy">
        <div className="flex flex-col gap-3">
          <Eyebrow text={S.eyebrow} />
          <h1 className="text-2xl font-semibold">{S.refused.screenHeading}</h1>
        </div>
        <div
          role="status"
          aria-live="polite"
          className="rounded-lg border border-error/40 bg-error/10 p-4 text-base"
        >
          <p className="font-semibold">{S.refused.heading}</p>
          <p className="mt-1">{S.refused.body}</p>
        </div>
        {runCount > 0 && (
          <p className="text-sm text-dream-navy/60">{S.refused.attemptIndicator(runCount)}</p>
        )}
        <div className="flex flex-col gap-3 border-t border-stroke-on-dark/20 pt-4">
          {/*
            Refused-screen title input (Sebastian ruling 2026-09-10 late
            evening). Rendered ABOVE the description textarea so the
            customer sees both his inputs and can edit whichever caused
            the refusal. Seeded from envelope.title (= intake.titelwunsch
            at summary_review state). Passed to handleRetry as a
            titelwunschOverride; the backend writes it to
            intake.titelwunsch atomically alongside the retry flag.

            Affordance stack (Sebastian ruling 2026-09-11 morning) —
            byte-identical to the Personen block's title input at
            PersonenRoute.tsx:809-831: maxLength hard-stop at 40 chars,
            CharacterCounter underneath, released hint below. Sebastian's
            own defect 2026-09-11 09:57 (order 32b3a1e8) surfaced
            without these: he typed >40 chars, backend DTO validation
            rejected, PWA rendered the generic technical-failure copy
            instead of a length-specific message. Byte-identical here
            means the customer sees the same feedback the Personen path
            already gives him, from the same primitives.
          */}
          <label className="flex flex-col gap-2">
            <span className="text-sm font-medium">{S.refused.titleFieldLabel}</span>
            <input
              type="text"
              value={refusedTitle}
              onChange={(e) => setRefusedTitle(e.target.value)}
              aria-label={S.refused.titleFieldLabel}
              maxLength={TITELWUNSCH_MAX_LENGTH}
              disabled={retryPending}
              className="h-11 rounded-lg border border-stroke-on-dark/40 px-3 text-base"
            />
            <CharacterCounter
              value={refusedTitle.length}
              corridor={{ min: 0, max: TITELWUNSCH_MAX_LENGTH }}
            />
            <p className="text-xs text-dream-navy/70">
              {STRINGS.book.review.coverRegenTitleFieldHint}
            </p>
          </label>
          <label className="flex flex-col gap-2">
            <span className="text-sm font-medium">{S.refused.changeFieldLabel}</span>
            <div className="text-sm text-book-text-secondary">
              <p>{S.changeFieldExamplesLead}</p>
              <ul className="mt-1 space-y-0.5">
                <li>{S.refused.changeFieldExample1}</li>
                <li>{S.refused.changeFieldExample2}</li>
                <li>{S.refused.changeFieldExample3}</li>
              </ul>
            </div>
            <textarea
              value={customerChange}
              onChange={(e) => setCustomerChange(e.target.value)}
              placeholder={S.refused.changeFieldPlaceholder}
              aria-label={S.refused.changeFieldLabel}
              rows={4}
              maxLength={SUMMARY_CUSTOMER_CHANGE_MAX_LENGTH}
              disabled={retryPending}
              className="rounded-lg border border-stroke-on-dark/40 p-3 text-base"
            />
            <CharacterCounter
              value={customerChange.length}
              corridor={{ min: 0, max: SUMMARY_CUSTOMER_CHANGE_MAX_LENGTH }}
            />
          </label>
          {isLastRunRefused && (
            <p
              role="status"
              aria-live="polite"
              className="rounded-md bg-dream-gold/15 p-3 text-sm text-dream-navy"
            >
              {S.capWarningBeforeThirdRun}
            </p>
          )}
          {retryClickState.kind === 'error' && (
            <p role="alert" className="text-sm text-error">
              {retryClickState.message}
            </p>
          )}
          <button
            type="button"
            onClick={() => void handleRetry(refusedTitle)}
            disabled={
              retryPending ||
              // Enable when EITHER field has been edited from its
              // initial state. Requires at least one meaningful change
              // — submitting with both fields unchanged would just re-
              // run V7 on the same inputs and refuse again.
              (customerChange.trim().length === 0 &&
                refusedTitle.trim() === initialRefusedTitleRef.current.trim())
            }
            className="h-11 rounded-lg border border-dream-navy px-6 text-base font-medium text-dream-navy hover:bg-dream-navy/5 disabled:opacity-60"
          >
            {retryPending ? (
              <span className="inline-flex items-center gap-2">
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                {S.retryLoading}
              </span>
            ) : (
              S.retryButton
            )}
          </button>
        </div>
      </div>
    );
  }

  // ── State 2: generation-failed BEFORE first summary lands ─────────
  //
  // Block 3 · 3.1 + 3.2 (Sebastian ruling 2026-09-09) — eyebrow +
  // heading + book title. `envelope.title` is populated only when
  // the customer supplied a `titelwunsch` on Personen (summary_review
  // sits BEFORE anchor generation, so the anchor-title service has
  // not yet run — an empty title just skips the block).
  if (generationFailed && summaryText.length === 0) {
    return (
      <div className="mx-auto flex max-w-2xl flex-col gap-6 p-4 text-dream-navy">
        <div className="flex flex-col gap-3">
          <Eyebrow text={S.eyebrow} />
          <h1 className="text-2xl font-semibold">{S.heading}</h1>
          {envelope.title.length > 0 && (
            <p className="text-lg text-dream-navy/80">„{envelope.title}"</p>
          )}
        </div>
        <p
          role="status"
          aria-live="polite"
          className="rounded-lg border border-error/40 bg-error/10 p-4 text-base"
        >
          {S.generationFailed}
        </p>
        <button
          type="button"
          onClick={() => void handleRetry()}
          disabled={retryPending}
          className="h-12 rounded-lg bg-dream-gold px-8 text-base font-bold text-dream-navy hover:bg-dream-gold/90 disabled:opacity-60"
        >
          {retryPending ? (
            <span className="inline-flex items-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
              {S.retryLoading}
            </span>
          ) : (
            S.retryButton
          )}
        </button>
      </div>
    );
  }

  // ── State 3: generating (first summary not yet arrived) ───────────
  //
  // Block 2 · 2.10 · (Sebastian ruling 2026-09-09) reorder to text-
  // first, spinner-BELOW ("centred, with a spinner below" per spec).
  // Heading + body copy stays sourced from
  // `STRINGS.book.summaryReview.generatingHeading` /
  // `.generatingBody` — those two strings were rewritten in the same
  // ruling ("Wir lesen, was du uns erzählt hast" + "Das dauert einen
  // kurzen Moment.").
  //
  // Block 3 correction (Sebastian test-book finding 2026-09-09) —
  // `min-h-[24rem] justify-center` so the small loader block sits in
  // the middle of the BookLane card instead of hugging the top of a
  // nearly-empty white area. Same treatment as the light_gate_pending
  // WaitScreen via `centered` — the two loaders sit back-to-back in
  // the flow, they should look the same.
  if (summaryText.length === 0) {
    return (
      <div className="mx-auto flex min-h-[24rem] max-w-2xl flex-col items-center justify-center gap-4 p-8 text-center text-dream-navy">
        <h1 className="text-xl font-semibold">{S.generatingHeading}</h1>
        <p className="text-base text-dream-navy/80">{S.generatingBody}</p>
        <Loader2 className="h-8 w-8 animate-spin text-dream-navy/70" aria-hidden />
      </div>
    );
  }

  // ── State 4: ready-to-review (edit + change-request + release) ────
  //
  // Sebastian ruling 2026-09-21 (follow-up) — hierarchy: direct edit
  // is the PRIMARY affordance ("Zusammenfassung bearbeiten" gold
  // button, prefilled editor). Change request is a collapsed
  // alternative below, opened by the quieter link "Oder sag uns, was
  // anders sein soll". Shared 3-attempt budget across both paths;
  // after the third attempt both paths disappear and only "Passt so"
  // remains.
  const remainingAttempts = Math.max(0, SUMMARY_RUN_CAP - runCount);

  return (
    <div className="mx-auto flex max-w-2xl flex-col gap-6 p-4 text-dream-navy">
      <div className="flex flex-col gap-3">
        <Eyebrow text={S.eyebrow} />
        <h1 className="text-2xl font-semibold">{S.heading}</h1>
        {envelope.title.length > 0 && (
          <p className="text-lg text-dream-navy/80">„{envelope.title}"</p>
        )}
        <p className="text-base text-dream-navy/80">{S.subheading}</p>
      </div>
      <article className="rounded-lg bg-book-story-block p-4 text-base leading-relaxed">
        <p>{summaryText}</p>
      </article>
      {/*
        Sebastian ruling 2026-09-03 — State 4 must surface generationFailed.
        Order 595bb1d5 hit two-in-a-row schema-gate failures on a retry;
        without this banner the customer sees the OLD summary with no
        signal anything went wrong (presenting as "change ignored").
      */}
      {generationFailed && (
        <p
          role="status"
          aria-live="polite"
          className="rounded-lg border border-error/40 bg-error/10 p-4 text-base"
        >
          {S.generationFailed}
        </p>
      )}

      {/* Sebastian ruling 2026-09-21 (follow-up) — edit + change-request
          block. Counter line above the buttons; primary edit button
          first; collapsed change-request link second. After the third
          attempt both paths disappear and the cap-exhausted line
          replaces the counter. */}
      <div className="flex flex-col gap-3 border-t border-stroke-on-dark/20 pt-4">
        {capReached ? (
          <p className="text-sm text-dream-navy/80">{S.capExhaustedLine}</p>
        ) : (
          <>
            <p className="text-sm text-dream-navy/60">
              {S.attemptsRemainingLine(remainingAttempts)}
            </p>
            {/* Sebastian ruling 2026-09-21 (follow-up 2, Option A) —
                the edit button is a SMALLER, LEFT-ALIGNED SECONDARY
                affordance, clearly distinct from "Passt so" (which
                stays the only full-width gold button on the screen).
                `self-start` pulls the button off the flex column's
                stretch default so it sits at its natural width on
                the left; navy-bordered secondary style mirrors the
                change-request retry button below. */}
            {!(selfWriteOpen || selfWriteInFlight) && (
              <button
                type="button"
                onClick={openSelfWrite}
                disabled={selfWritePending}
                className="h-10 self-start rounded-lg border border-dream-navy px-4 text-sm font-medium text-dream-navy hover:bg-dream-navy/5 disabled:opacity-60"
              >
                {S.selfWrite.openEditorButton}
              </button>
            )}
            {(selfWriteOpen || selfWriteInFlight) && (
              <>
                <div className="flex flex-col gap-2">
                  <h2 className="text-lg font-semibold">{S.selfWrite.heading}</h2>
                  <p className="text-base text-dream-navy/80">{S.selfWrite.instruction}</p>
                </div>
                <label className="flex flex-col gap-2">
                  <span className="text-sm font-medium">{S.selfWrite.textareaLabel}</span>
                  <textarea
                    value={selfWriteText}
                    onChange={(e) => setSelfWriteText(e.target.value)}
                    placeholder={S.selfWrite.textareaPlaceholder}
                    aria-label={S.selfWrite.textareaLabel}
                    rows={8}
                    // fix/text-field-limits (Sebastian ruling 2026-09-29) —
                    // hard cap on typing + pasting. Note: React does NOT
                    // truncate a prefilled value set via state, so an
                    // over-limit model summary (see envelope.zusammenfassung
                    // prefill in openSelfWrite above) renders in full and
                    // the customer can shorten it. Save is blocked while
                    // over-limit via the counter's red state + the
                    // overLimitBlockingMessage below.
                    maxLength={SUMMARY_TEXT_MAX_LENGTH}
                    disabled={selfWritePending}
                    className="rounded-lg border border-stroke-on-dark/40 p-3 text-base"
                  />
                  <CharacterCounter
                    value={selfWriteText.length}
                    corridor={{ min: 0, max: SUMMARY_TEXT_MAX_LENGTH }}
                  />
                  <span className="text-xs text-dream-navy/60">{S.selfWrite.charHint}</span>
                </label>
                {selfWriteText.length > SUMMARY_TEXT_MAX_LENGTH && (
                  <p role="alert" className="text-sm text-error">
                    {STRINGS.book.textLimits.overLimitBlockingMessage.replace(
                      '{max}',
                      SUMMARY_TEXT_MAX_LENGTH.toString(),
                    )}
                  </p>
                )}
                {!selfWritePending &&
                  (selfWriteClickState.kind === 'error' ||
                    envelope.summaryReview?.selfWriteRefused === true) && (
                    <p role="alert" className="text-sm text-error">
                      {selfWriteClickState.kind === 'error'
                        ? selfWriteClickState.message
                        : S.selfWrite.contentRefusedLine}
                    </p>
                  )}
                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={() => void handleSelfWrite()}
                    disabled={
                      selfWritePending ||
                      selfWriteText.trim().length === 0 ||
                      selfWriteText.length > SUMMARY_TEXT_MAX_LENGTH
                    }
                    className="h-11 rounded-lg bg-dream-gold px-6 text-base font-bold text-dream-navy hover:bg-dream-gold/90 disabled:opacity-60"
                  >
                    {selfWritePending ? (
                      <span className="inline-flex items-center gap-2">
                        <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                        {S.selfWriteLoading}
                      </span>
                    ) : (
                      S.selfWrite.submitButton
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={closeSelfWrite}
                    disabled={selfWritePending}
                    className="h-11 rounded-lg border border-dream-navy px-6 text-base font-medium text-dream-navy hover:bg-dream-navy/5 disabled:opacity-60"
                  >
                    {S.selfWrite.cancelButton}
                  </button>
                </div>
              </>
            )}

            {/* Change-request area — collapsed alternative. Only
                available when the customer is NOT currently in the
                edit path AND has NOT yet self-edited the summary
                (Sebastian ruling 2026-09-21 follow-up 2, Option A —
                once a self-write has landed the change-request path
                disappears entirely). Opens on click of the quieter
                link. When `retryInFlight === true` the area auto-opens
                so the customer sees the loading state on the submit
                button (mirror of the self-write auto-open on
                `selfWriteInFlight`). */}
            {!selfWriteOpen &&
              !selfWriteInFlight &&
              !changeRequestOpen &&
              !retryInFlight &&
              !selfWriteHasOccurred && (
                <button
                  type="button"
                  onClick={openChangeRequest}
                  className="self-start text-sm font-medium text-dream-navy underline underline-offset-4 hover:text-dream-navy/80"
                >
                  {S.openChangeRequestButton}
                </button>
              )}
            {!selfWriteOpen &&
              !selfWriteInFlight &&
              !selfWriteHasOccurred &&
              (changeRequestOpen || retryInFlight) && (
              <label className="flex flex-col gap-2">
                <span className="text-sm font-medium">{S.changeFieldLabel}</span>
                <div className="text-sm text-book-text-secondary">
                  <p>{S.changeFieldExamplesLead}</p>
                  <ul className="mt-1 space-y-0.5">
                    <li>{S.changeFieldExample1}</li>
                    <li>{S.changeFieldExample2}</li>
                    <li>{S.changeFieldExample3}</li>
                  </ul>
                </div>
                <textarea
                  value={customerChange}
                  onChange={(e) => setCustomerChange(e.target.value)}
                  placeholder={S.changeFieldPlaceholder}
                  aria-label={S.changeFieldLabel}
                  rows={4}
                  maxLength={SUMMARY_CUSTOMER_CHANGE_MAX_LENGTH}
                  disabled={retryPending}
                  className="rounded-lg border border-stroke-on-dark/40 p-3 text-base"
                />
                <CharacterCounter
                  value={customerChange.length}
                  corridor={{ min: 0, max: SUMMARY_CUSTOMER_CHANGE_MAX_LENGTH }}
                />
                {retryClickState.kind === 'error' && (
                  <p role="alert" className="text-sm text-error">
                    {retryClickState.message}
                  </p>
                )}
                <button
                  type="button"
                  onClick={() => void handleRetry()}
                  disabled={retryPending || customerChange.trim().length === 0}
                  className="h-11 rounded-lg border border-dream-navy px-6 text-base font-medium text-dream-navy hover:bg-dream-navy/5 disabled:opacity-60"
                >
                  {retryPending ? (
                    <span className="inline-flex items-center gap-2">
                      <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                      {S.retryLoading}
                    </span>
                  ) : (
                    S.retryButton
                  )}
                </button>
              </label>
            )}
          </>
        )}
      </div>

      {/* Release surface — approveNotice sits ABOVE the button so the
          customer sees it before pressing (Sebastian's rule). Sebastian
          ruling 2026-09-17 — an unverified customer sees the
          `VerificationPendingBlock` next to a disabled release button
          (poll flips `customerEmailVerified` server-side once the
          verify link is clicked; the existing 4s poll cadence is the
          learn-signal, no new poller). */}
      <div className="flex flex-col gap-3 border-t border-stroke-on-dark/20 pt-4">
        <p className="text-sm text-dream-navy/80">{S.approveNotice}</p>
        {envelope.customerEmailVerified === false && (
          <VerificationPendingBlock customerEmail={envelope.customerEmail} />
        )}
        {releaseState.kind === 'error' && (
          <p role="alert" className="text-sm text-error">
            {releaseState.message}
          </p>
        )}
        <button
          type="button"
          onClick={() => void handleRelease()}
          disabled={
            releaseState.kind === 'pending' ||
            retryPending ||
            // W13 (Sebastian live ~09.10.2026): the release button
            // was clickable while a self-write submission was still
            // in flight ("adoption of a generated/suggested text in
            // progress"). Pre-fix the disabled condition only covered
            // the retry/change-request path, leaving self-write
            // uncaught.
            selfWritePending ||
            envelope.customerEmailVerified === false
          }
          className="h-12 rounded-lg bg-dream-gold px-8 text-base font-bold text-dream-navy hover:bg-dream-gold/90 disabled:opacity-60"
        >
          {releaseState.kind === 'pending' ? (
            <span className="inline-flex items-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
              {S.releaseLoading}
            </span>
          ) : (
            S.approveButton
          )}
        </button>
      </div>
    </div>
  );
}
